import { DIALOG_DATA } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { InsightsService } from 'libs/shared/domain/src/lib/infrastructure/insights.service';
import { TrackingEvent } from 'libs/shared/domain/src/lib/infrastructure/tracking-events';

import { NodeDetailPopoverData, NodeDetailPopoverTab, POPOVER_DATA } from '@treetop/shared/model';

import { NodeDetailPopoverAttachmentsComponent } from './node-detail-popover-attachments/node-detail-popover-attachments.component';
import { NodeDetailPopoverDescriptionComponent } from './node-detail-popover-description/node-detail-popover-description.component';
import { NodeDetailPopoverLinksComponent } from './node-detail-popover-links/node-detail-popover-links.component';
import { NodeDetailPopoverMedicationsComponent } from './node-detail-popover-medications/node-detail-popover-medications.component';
import { NodeDetailPopoverReferencesComponent } from './node-detail-popover-references/node-detail-popover-references.component';

@Component({
  selector: 'tt-node-detail-popover',
  standalone: true,
  imports: [
    TranslocoModule,
    NodeDetailPopoverDescriptionComponent,
    NodeDetailPopoverAttachmentsComponent,
    NodeDetailPopoverLinksComponent,
    NodeDetailPopoverMedicationsComponent,
    NodeDetailPopoverReferencesComponent,
  ],
  templateUrl: './node-detail-popover.component.html',
  styleUrl: './node-detail-popover.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NodeDetailPopoverComponent {
  private popoverData: NodeDetailPopoverData = inject(POPOVER_DATA, { optional: true });
  protected dialogData: NodeDetailPopoverData = inject(DIALOG_DATA, { optional: true });
  private insightsService = inject(InsightsService);

  protected tabs: NodeDetailPopoverTab[] = [];
  protected selectedTab: WritableSignal<NodeDetailPopoverTab>;

  protected data: NodeDetailPopoverData = this.dialogData ?? this.popoverData;

  constructor() {
    if (this.data.description?.description || this.data.description?.recurrence || this.data.description?.recurrenceInterval) {
      this.tabs.push(NodeDetailPopoverTab.DESCRIPTION);
    }

    if (this.data.attachments?.length > 0 || this.data.localAttachments?.length > 0) {
      this.tabs.push(NodeDetailPopoverTab.ATTACHMENTS);
    }

    if (this.data.links?.length > 0) {
      this.tabs.push(NodeDetailPopoverTab.LINKS);
    }

    if (this.data.medications?.length > 0) {
      this.tabs.push(NodeDetailPopoverTab.MEDICATIONS);
    }

    if (this.data.references?.length > 0) {
      this.tabs.push(NodeDetailPopoverTab.REFERENCES);
    }

    this.selectedTab = signal(this.data.selectedTab ?? this.tabs[0]);

    if (this.selectedTab()) {
      this.insightsService.trackEvent(TrackingEvent.NODE_DETAIL_POPOVER_TAB_VIEWED, {
        tab: this.selectedTab(),
        availableTabs: this.tabs,
        nodeTitle: this.data.nodeTitle,
      });
    }
  }

  protected onTabSwitch(tab: NodeDetailPopoverTab): void {
    const previousTab = this.selectedTab();
    this.selectedTab.set(tab);

    if (previousTab !== tab) {
      this.insightsService.trackEvent(TrackingEvent.NODE_DETAIL_POPOVER_TAB_VIEWED, {
        tab: tab,
        availableTabs: this.tabs,
        nodeTitle: this.data.nodeTitle,
      });
    }
  }

  protected readonly NodeDetailPopoverTab = NodeDetailPopoverTab;
}
